/**
 * Silver Palace Inc. – Wishlist
 * Stores saved product handles in localStorage under 'sp_wishlist'.
 * Works on product pages (heart button) and the customer wishlist page.
 */

const SP_WISHLIST_KEY = 'sp_wishlist';

const Wishlist = {
  /** @returns {string[]} array of product handles */
  get() {
    try {
      return JSON.parse(localStorage.getItem(SP_WISHLIST_KEY) || '[]');
    } catch {
      return [];
    }
  },

  /** @param {string[]} handles */
  set(handles) {
    localStorage.setItem(SP_WISHLIST_KEY, JSON.stringify(handles));
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { handles } }));
  },

  /** @param {string} handle */
  add(handle) {
    const list = this.get();
    if (!list.includes(handle)) {
      list.push(handle);
      this.set(list);
    }
  },

  /** @param {string} handle */
  remove(handle) {
    this.set(this.get().filter(h => h !== handle));
  },

  /** @param {string} handle @returns {boolean} */
  has(handle) {
    return this.get().includes(handle);
  },

  /** @param {string} handle */
  toggle(handle) {
    this.has(handle) ? this.remove(handle) : this.add(handle);
  },

  count() {
    return this.get().length;
  }
};

/* ─── Wishlist Button (product page & product cards) ─────────────────────── */

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.handle;
    this.btn = this.querySelector('[data-wishlist-btn]');
    if (!this.btn || !this.handle) return;

    this._sync();
    this.btn.addEventListener('click', () => this._toggle());

    document.addEventListener('wishlist:updated', () => this._sync());
  }

  _sync() {
    const saved = Wishlist.has(this.handle);
    this.btn.classList.toggle('is-saved', saved);
    this.btn.setAttribute('aria-label', saved ? 'Remove from saved items' : 'Save this item');
    this.btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
  }

  _toggle() {
    Wishlist.toggle(this.handle);
    this._sync();

    // Brief animation feedback
    this.btn.classList.add('wishlist-btn--pop');
    this.btn.addEventListener('animationend', () => {
      this.btn.classList.remove('wishlist-btn--pop');
    }, { once: true });
  }
}

customElements.define('wishlist-button', WishlistButton);

/* ─── Wishlist Page ───────────────────────────────────────────────────────── */

class WishlistPage extends HTMLElement {
  connectedCallback() {
    this._render();
    document.addEventListener('wishlist:updated', () => this._render());
  }

  async _render() {
    const handles = Wishlist.get();
    const grid = this.querySelector('[data-wishlist-grid]');
    const empty = this.querySelector('[data-wishlist-empty]');
    const countEl = this.querySelector('[data-wishlist-count]');

    if (!grid) return;

    if (countEl) countEl.textContent = handles.length;

    if (handles.length === 0) {
      grid.innerHTML = '';
      grid.style.display = 'none';
      if (empty) empty.style.display = '';
      return;
    }

    if (empty) empty.style.display = 'none';
    grid.style.display = '';

    // Fetch product data for each handle via Shopify's product JSON endpoint
    const products = await Promise.all(
      handles.map(handle =>
        fetch(`/products/${handle}.js`)
          .then(r => r.ok ? r.json() : null)
          .catch(() => null)
      )
    );

    const validProducts = products.filter(Boolean);

    // Remove any handles whose products no longer exist
    const validHandles = validProducts.map(p => p.handle);
    if (validHandles.length !== handles.length) {
      Wishlist.set(validHandles);
    }

    grid.innerHTML = validProducts.map(p => this._cardHTML(p)).join('');

    // Attach remove listeners
    grid.querySelectorAll('[data-remove-handle]').forEach(btn => {
      btn.addEventListener('click', () => {
        Wishlist.remove(btn.dataset.removeHandle);
      });
    });
  }

  _cardHTML(p) {
    const variant = p.variants[0];
    const price = variant
      ? (variant.price / 100).toLocaleString('en-US', { style: 'currency', currency: p.currency || 'USD' })
      : '';
    const img = p.featured_image
      ? `<img src="${p.featured_image}" alt="${this._esc(p.title)}" class="wishlist-card__img" loading="lazy">`
      : `<div class="wishlist-card__img-placeholder"></div>`;

    return `
      <div class="wishlist-card" data-handle="${this._esc(p.handle)}">
        <a href="/products/${this._esc(p.handle)}" class="wishlist-card__image-link">
          ${img}
        </a>
        <div class="wishlist-card__body">
          <a href="/products/${this._esc(p.handle)}" class="wishlist-card__title">${this._esc(p.title)}</a>
          <span class="wishlist-card__price">${price}</span>
          <div class="wishlist-card__actions">
            <a href="/products/${this._esc(p.handle)}" class="button button--primary wishlist-card__btn-view">View Product</a>
            <button class="wishlist-card__btn-remove" data-remove-handle="${this._esc(p.handle)}" aria-label="Remove ${this._esc(p.title)} from saved items">
              <svg viewBox="0 0 14 14" aria-hidden="true" focusable="false" width="14" height="14">
                <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
              Remove
            </button>
          </div>
        </div>
      </div>`;
  }

  _esc(str) {
    return String(str).replace(/[&<>"']/g, c =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );
  }
}

customElements.define('wishlist-page', WishlistPage);

/* ─── Wishlist count badges (optional, for header icon) ─────────────────── */

function updateWishlistBadges() {
  const count = Wishlist.count();
  document.querySelectorAll('[data-wishlist-badge]').forEach(el => {
    el.textContent = count;
    el.style.display = count > 0 ? '' : 'none';
  });
}

document.addEventListener('wishlist:updated', updateWishlistBadges);
document.addEventListener('DOMContentLoaded', updateWishlistBadges);
